import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Box,
  useMediaQuery,
  TextField,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import EditAvmPermissionsModal from 'src/components/modals/EditAvmPermissionsModal';
import EditButton from 'src/components/shared/EditButton';
import Iconify from 'src/components/iconify';

const AvmPermissionsManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mallList, setMallList] = useState([]);
  const [filteredMallList, setFilteredMallList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedMall, setSelectedMall] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch malls on component mount
  useEffect(() => {
    fetchMalls();
  }, []);

  // Filter malls based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredMallList(mallList);
    } else {
      const filtered = mallList.filter(mall =>
        mall.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        mall.city?.toLowerCase().includes(searchText.toLowerCase()) ||
        mall.county?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredMallList(filtered);
    }
  }, [searchText, mallList]);

  const fetchMalls = async () => {
    setLoading(true);
    try {
      const response = await api.post('/malls/get-malls', {
        withOffices: true,
      });

      if (response.data && response.data.result) {
        setMallList(response.data.result);
        setFilteredMallList(response.data.result);
      } else {
        toast.error('Mağazalar yüklenemedi.');
      }
    } catch (error) {
      console.error('Error fetching malls:', error);
      toast.error('Mağazalar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (mall) => {
    setSelectedMall(mall);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMall(null);
  };

  const handleSavePermissions = () => {
    fetchMalls(); // Refresh the list
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>AVM Yönetim Yetkileri</title>
      </Helmet>

      <Stack sx={{ paddingInline: '25px' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            AVM Yönetim Yetkileri
          </Typography>
        </Stack>

        {/* Search Bar */}
        <Stack mb={3}>
          <FormControl sx={{ minWidth: '100%', maxWidth: '400px' }}>
            <TextField
              label="AVM Ara"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="AVM adı, şehir veya ilçe ile ara..."
              InputProps={{
                startAdornment: (
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ mr: 1, color: 'text.disabled' }}
                  />
                ),
              }}
            />
          </FormControl>
        </Stack>

        {/* Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>AVM Adı</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Şehir</TableCell>}
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }}>İlçe</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMallList.length > 0 ? (
                  filteredMallList.map((mall) => (
                    <TableRow key={mall.id} hover>
                      <TableCell>{mall.title}</TableCell>
                      {!isMobile && <TableCell>{mall.city}</TableCell>}
                      {!isMobile && <TableCell>{mall.county}</TableCell>}
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEditPermissions(mall)}
                          sx={{
                            backgroundColor: '#079455',
                            '&:hover': {
                              backgroundColor: '#067041',
                            },
                          }}
                        >
                          Yetkileri Düzenle
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 2 : 4} align="center" sx={{ py: 3 }}>
                      <Typography color="textSecondary">
                        Sonuç bulunamadı
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>

      {/* Edit Permissions Modal */}
      {selectedMall && (
        <EditAvmPermissionsModal
          open={modalOpen}
          onClose={handleCloseModal}
          mallData={selectedMall}
          onSave={handleSavePermissions}
        />
      )}
    </>
  );
};

export default AvmPermissionsManagement;

